// Wohler Chemical — Phase 6 Research Profiles: Biased MOR / Safer Opioids (Program D)
//
// Primary references:
//   Manglik A., Lin H., Aryal D.K. et al. "Structure-based discovery of opioid analgesics
//   with reduced side effects." Nature 2016, 537, 185-190. (PZM21)
//   DOI: 10.1038/nature19112
//
//   DeWire S.M., Yamashita D.S., Rominger D.H. et al. "A G protein-biased ligand at the
//   mu-opioid receptor is potently analgesic with reduced gastrointestinal and respiratory
//   dysfunction compared with morphine." J. Pharmacol. Exp. Ther. 2013, 344, 708-717.
//   (TRV130 / oliceridine)
//
//   Schmid C.L. et al. "Bias Factor and Therapeutic Window Correlate to Predict Safer
//   Opioid Analgesics." Cell 2017, 171, 1165-1175. (SR-14968 / SR-17018)
//
//   Kruegel A.C. et al. "7-Hydroxymitragynine Is an Active Metabolite of Mitragynine."
//   ACS Cent. Sci. 2019 (biased opioid pharmacology context)
//
//   Kaserer T. et al. "Structural and pharmacological characterization of cebranopadol."
//   Drug Discov. Today 2020 (NOP/MOR dual agonist)
//
//   Kieffer B.L., Gavériaux-Ruff C. "Exploring the opioid system by gene knockout."
//   Prog. Neurobiol. 2002 (peripheral KOR analgesia concept)
//
// Wohler standard solvents only: EtOAc, THF, Hexane, Heptane, Pentane, EtOH, MeOH, IPA, Acetone

export const opioidResearchProfiles = {

  "pzm21": {
    id: "pzm21",
    name: "PZM21",
    fullName: "PZM21 — structure-based biased mu-opioid receptor agonist",
    aliases: ["PZM-21"],
    categoryId: "opioid",
    program: "Program D — Biased MOR / Safer Opioid",
    cas: "1997387-43-5",
    formula: "C21H25N3O2",
    molecularWeight: 367.45,
    schedulingStatus: "Unscheduled research chemical (investigational)",
    schedulingNote: "PZM21 is not currently scheduled; as a MOR agonist with analgesic activity, any clinical development would likely trigger Schedule II placement.",
    benefitScore: 72,
    harmScore: 45,
    evidenceConfidence: "MEDIUM",
    status: "Priority 1",
    statusColor: "green",
    goDecision: "GO — structural template for G-protein biased MOR program",
    priorityTier: "Tier 1 — Active Research",
    summary: "PZM21 was discovered by Manglik, Shoichet, and Roth via virtual screening of 3 million compounds against the MOR crystal structure, followed by SAR optimization. Originally reported as a G-protein biased MOR agonist with analgesia but reduced respiratory depression and reward. Subsequent re-evaluation (Hill et al. 2018, Kliewer et al. 2019) showed partial agonism rather than strong bias, but the scaffold remains valuable as a low-efficacy MOR template.",
    pros: [
      "Derived from structure-based drug design — defined binding pose (PDB 5C1M)",
      "Low-efficacy partial MOR agonism reduces respiratory depression and reward",
      "Clean off-target profile vs morphine (no kappa/delta/NOP at therapeutic doses)",
      "Oral bioavailability and CNS penetration confirmed in rodent PK",
      "Open IP landscape — no NCE restrictions on core scaffold",
      "Template for designing low-efficacy MOR ligands that avoid β-arrestin-dependent toxicities"
    ],
    cons: [
      "Original bias claims overstated — re-classification as partial agonist limits narrative",
      "Analgesic potency modest (ED50 ~4.6 mg/kg IP, rat hot plate)",
      "Short half-life in rodents (t½ ~1.5 h) requires PK optimization",
      "Multi-step synthesis (6+ steps) compared to morphinans",
      "No human clinical data"
    ],
    pharmacology: {
      mechanismOfAction: "Low-efficacy partial agonism at MOR with minimal β-arrestin-2 recruitment. The reduced efficacy at both G-protein and β-arrestin pathways (rather than true bias) limits maximal respiratory depression while preserving spinal/supraspinal analgesia. Selectivity >1000× for MOR over KOR/DOR/NOP.",
      targets: [
        { target: "MOR (mu-opioid)", affinity: "Ki = 1.1 nM", activity: "Partial agonist (EC50 = 42 nM, Emax ~60-70% of DAMGO at G-protein)" },
        { target: "KOR", affinity: "Ki >1 μM", activity: "Inactive" },
        { target: "DOR", affinity: "Ki >1 μM", activity: "Inactive" },
        { target: "NOP", affinity: "Ki >10 μM", activity: "Inactive" },
        { target: "β-arrestin-2 recruitment", affinity: "EC50 >1 μM", activity: "Very weak — bias factor ~3 vs DAMGO" },
        { target: "hERG", affinity: "IC50 >30 μM", activity: "Clean" }
      ],
      onsetDuration: "Rodent IP: onset 15-30 min; t½ ~1.5 h; analgesic duration ~2-3 h at 10 mg/kg",
      metabolism: "CYP3A4-mediated N-dealkylation and CYP2D6-mediated aromatic hydroxylation; multiple minor metabolites; no reactive metabolite liabilities flagged in Manglik 2016."
    },
    labSynthesis: {
      overview: "Six-step synthesis from commercially available 3-methoxyphenethylamine and a substituted urea coupling partner per Manglik 2016 Nature supplementary information.",
      steps: [
        "Step 1 — N-Boc protection of 3-methoxyphenethylamine with Boc2O in THF/H2O, 2 h, RT (95%).",
        "Step 2 — α-alkylation: LDA (1.1 eq) in THF at -78°C, then alkyl halide coupling partner (1.2 eq). Warm to RT, 4 h.",
        "Step 3 — Boc deprotection with 4M HCl in EtOAc, 2 h RT. Free base extracted with EtOAc/sat. NaHCO3.",
        "Step 4 — Urea formation: treat amine with triphosgene (0.35 eq) in THF at 0°C, then second amine (1.1 eq) with DIPEA (3 eq). Warm to RT, 12 h.",
        "Step 5 — Cyclization: heat intermediate in EtOH with K2CO3 (2 eq) at reflux, 6 h.",
        "Step 6 — Silica column (Hexane/EtOAc → EtOAc/MeOH gradient), then crystallize from EtOH/Heptane. Isolate (S)-PZM21 by chiral SFC if needed. Overall 18-25% over 6 steps."
      ],
      analytics: "1H/13C NMR (CDCl3 and DMSO-d6), HRMS [M+H]+ = 368.2, HPLC ≥99%, chiral HPLC (confirms ee ≥99%), mp characterization."
    },
    kgScaleSynthesis: {
      overview: "Kg-scale PZM21 synthesis requires development to avoid triphosgene (LEL/toxicity concerns) by substituting CDI (carbonyldiimidazole) for urea formation. Wohler standard-solvent-only protocol.",
      targetScale: "500 g PZM21 free base per campaign, ≥99.5% HPLC purity, chiral ee ≥99.5%",
      steps: [
        "Step 1 — Boc protection of 3-methoxyphenethylamine (2.0 kg, 13.2 mol) in THF/H2O with Boc2O (1.05 eq) and Na2CO3. 2 h RT. Extract with EtOAc, dry, concentrate.",
        "Step 2 — α-Alkylation: dissolve Boc-amine in anhydrous THF (30 L), cool to -70°C, add n-BuLi (1.1 eq) slowly. Stir 30 min. Add alkyl halide coupling partner (1.2 eq) in THF. Warm to -40°C over 2 h, then RT.",
        "Step 3 — Quench with saturated NH4Cl at 0°C. Extract with EtOAc (3 × 20 L). Wash with brine, dry over Na2SO4, concentrate.",
        "Step 4 — Boc removal: dissolve in EtOAc (20 L), add 4M HCl in EtOAc (3 eq) slowly at 0°C. Stir 2 h. Concentrate, slurry in Acetone, filter HCl salt.",
        "Step 5 — Free-base amine: slurry HCl salt in EtOAc/saturated NaHCO3, separate organic, dry, concentrate.",
        "Step 6 — Urea formation via CDI route (safer than triphosgene): dissolve amine in anhydrous THF, add CDI (1.1 eq) at 0°C. Stir 1 h. Add second amine partner (1.1 eq) with DIPEA (2 eq). Stir 16 h at RT.",
        "Step 7 — Workup: dilute with EtOAc, wash with 1M HCl (to remove excess amine), then sat. NaHCO3, brine. Dry, concentrate.",
        "Step 8 — Cyclization: dissolve in EtOH (15 L), add K2CO3 (2 eq), reflux 6 h. Cool, filter salts, concentrate.",
        "Step 9 — Chromatography (for first kg campaign) or direct crystallization from EtOH/Heptane. Optional chiral SFC resolution for (S)-enantiomer.",
        "Step 10 — Final crystallization from EtOH/Heptane (1:4), cool slowly to 5°C, filter, wash with cold Heptane, dry at 45°C under vacuum.",
        "Step 11 — Release testing: HPLC ≥99.5%, chiral HPLC ≥99.5% ee, residual solvents (ICH Q3C), heavy metals, DSC mp."
      ],
      yieldTarget: "20-28% overall across 6 steps (500 g PZM21 per campaign)",
      standardSolventsUsed: ["THF", "EtOAc", "Heptane", "EtOH", "Acetone"],
      standardSolventsNotUsed: ["DCM replaced by EtOAc throughout", "DMF replaced by THF for urea formation", "Triphosgene replaced by CDI"],
      hazards: "n-BuLi: pyrophoric, requires dedicated cryo-reactor with N2 blanketing. Boc2O at scale: CO2 evolution — vent sizing critical. CDI moisture-sensitive — dry THF. Final API is a MOR agonist — handle per Schedule II-equivalent containment (Band 4 pending DEA status)."
    },
    safety: {
      ehs: "OEL ≤ 1 μg/m³ (opioid-class default); Containment Band 4 for final API. Naloxone stations mandatory in processing areas.",
      toxicology: "Acute oral LD50 (rat) estimated 200-400 mg/kg. Respiratory depression at >30 mg/kg (vs morphine ~5 mg/kg). No GI motility inhibition at analgesic doses. No conditioned place preference in mice (reward test). Ames negative.",
      scheduling: "Unscheduled. Any clinical development would trigger Schedule II placement (CSA Section 201)."
    },
    wohlerScoring: {
      benefit: 72,
      harm: 45,
      net: 27,
      evidenceConfidence: "MEDIUM",
      rationale: "PZM21 retains strong value as a structurally well-characterized low-efficacy MOR ligand even after bias-claim revision. Usefulness is as a chemical template for Wohler's biased MOR library rather than a standalone drug candidate. Harm score reflects residual opioid-class risks and Schedule II trajectory."
    },
    references: [
      "Manglik A. et al. Nature 2016, 537, 185-190 (discovery + primary pharmacology)",
      "Hill R. et al. Br. J. Pharmacol. 2018, 175, 2653 (low-efficacy reclassification)",
      "Kliewer A. et al. Br. J. Pharmacol. 2019, 176, 3110 (bias reassessment)",
      "Schmid C.L. et al. Cell 2017, 171, 1165 (bias factor framework)",
      "PDB 5C1M (MOR crystal structure used for discovery)"
    ],
    nextSteps: [
      "Reproduce 6-step synthesis at 10 g scale",
      "Develop CDI-based kg-scale route (eliminate triphosgene)",
      "Chiral SFC method development for (S)-PZM21 isolation",
      "Rat hot-plate and tail-flick analgesia benchmarking vs morphine",
      "Plethysmography for respiratory depression window",
      "Use as reference compound for Wohler MOR library SAR"
    ]
  },

  "sr-14968": {
    id: "sr-14968",
    name: "SR-14968",
    fullName: "SR-14968 — Scripps biased MOR agonist (next-gen of SR-17018 class)",
    aliases: ["SR14968"],
    categoryId: "opioid",
    program: "Program D — Biased MOR / Safer Opioid",
    cas: "Not CAS-registered",
    formula: "C22H27FN4O2",
    molecularWeight: 398.48,
    schedulingStatus: "Unscheduled research chemical (investigational)",
    schedulingNote: "SR-14968 and related Scripps MOR biased agonists are not scheduled. Any clinical advancement would likely trigger Schedule II placement.",
    benefitScore: 74,
    harmScore: 40,
    evidenceConfidence: "MEDIUM",
    status: "Priority 1",
    statusColor: "green",
    goDecision: "GO — strongest G-protein bias reported in MOR literature",
    priorityTier: "Tier 1 — Active Research",
    summary: "SR-14968 (and sibling SR-17018) are Scripps biased MOR agonists reported by Bohn/Cameron/Schmid in Cell 2017. Bias factor 11-15 (vs DAMGO), the highest reported for a small-molecule MOR ligand. In vivo: equianalgesic to morphine but with markedly reduced respiratory depression, constipation, tolerance, and physical dependence. Represents the most pharmacologically validated MOR-biased scaffold.",
    pros: [
      "Highest G-protein bias factor reported in small-molecule MOR literature (Schmid 2017)",
      "Reduced respiratory depression, constipation, tolerance, and dependence in rodent models",
      "Equianalgesic potency to morphine — no efficacy compromise",
      "Non-morphinan scaffold avoids opiate alkaloid IP clutter",
      "Open IP — Scripps-licensable/design-around friendly"
    ],
    cons: [
      "Original bias framework under active debate (Hill 2018, Kliewer 2019)",
      "Synthesis is 7+ steps with expensive fluorinated intermediates",
      "Limited in vivo data beyond Bohn lab publications",
      "No human PK or safety data",
      "Metabolic liability at the piperazine linker"
    ],
    pharmacology: {
      mechanismOfAction: "Strong G-protein biased MOR agonism with minimal β-arrestin-2 recruitment. Bias factor ~11-15 vs DAMGO reference. Analgesia via G-protein-coupled downstream signaling (Gαi/o → K+ channel opening, Ca2+ channel inhibition) while avoiding β-arrestin-dependent recruitment of GRK3/6 and downstream respiratory/GI adverse effects.",
      targets: [
        { target: "MOR (mu-opioid)", affinity: "Ki = 6 nM", activity: "Full agonist at G-protein (Emax ~85% of DAMGO)" },
        { target: "β-arrestin-2", affinity: "EC50 >3 μM", activity: "Weak recruitment — bias factor ~11-15" },
        { target: "KOR", affinity: "Ki >1 μM", activity: "Inactive" },
        { target: "DOR", affinity: "Ki >1 μM", activity: "Inactive" },
        { target: "NOP", affinity: "Ki >10 μM", activity: "Inactive" },
        { target: "hERG", affinity: "IC50 >10 μM", activity: "Acceptable margin vs therapeutic" }
      ],
      onsetDuration: "Mouse SC: onset 15 min; t½ ~3-4 h; analgesic duration ~4-6 h",
      metabolism: "CYP3A4 and CYP2D6 mediate oxidative metabolism at the piperazine nitrogen and aromatic fluoroindole; glucuronidation is minor."
    },
    labSynthesis: {
      overview: "Seven-step synthesis per Schmid 2017 supplementary. Key disconnection: late-stage SNAr coupling of a fluoro-indole fragment with a substituted piperazine.",
      steps: [
        "Step 1 — Synthesize the fluoroindole core via Fischer indolization (4-fluorophenylhydrazine + ketone, H2SO4/EtOH, reflux).",
        "Step 2 — N1 protection (Boc2O, DMAP, THF) — pivoting to trityl for Wohler route to avoid DMF requirement.",
        "Step 3 — C2 bromination (NBS in EtOAc, 0°C, radical initiator).",
        "Step 4 — Suzuki coupling with arylboronic acid (Pd(PPh3)4, K2CO3, EtOH/H2O).",
        "Step 5 — N-deprotection (TFA in EtOAc for Boc, or acid for trityl).",
        "Step 6 — SNAr / reductive amination with N-Boc-piperazine derivative in THF with DIPEA.",
        "Step 7 — Final deprotection (4M HCl in EtOAc), free-basing, silica column (EtOAc/MeOH/Et3N), and HCl salt crystallization from EtOH/Acetone."
      ],
      analytics: "1H/13C/19F NMR (DMSO-d6), HRMS [M+H]+ = 399.2, HPLC ≥99%, chiral HPLC if stereocenter present."
    },
    kgScaleSynthesis: {
      overview: "Kg-scale SR-14968 via 7-step convergent synthesis. Critical Wohler adaptations: (1) trityl N-protection instead of Boc (eliminates TFA deprotection), (2) Suzuki coupling in EtOH/H2O rather than dioxane, (3) direct HCl salt crystallization from EtOH/Acetone.",
      targetScale: "300 g SR-14968 HCl per campaign, ≥99.5% HPLC purity",
      steps: [
        "Step 1 — Fischer indolization: 4-fluorophenylhydrazine HCl (2.0 kg) + methyl ketone (1.1 eq) in EtOH (25 L) with H2SO4 (0.3 eq). Reflux 5 h. Neutralize, extract with EtOAc, dry, concentrate. Crystallize from EtOH/Heptane.",
        "Step 2 — N1 tritylation: dissolve indole in anhydrous THF (15 L), add NaH (1.2 eq), stir 30 min, add trityl chloride (1.1 eq). Stir RT 6 h. Quench NH4Cl, extract EtOAc, dry, concentrate.",
        "Step 3 — C2 bromination: dissolve in EtOAc (20 L), add NBS (1.05 eq) and AIBN (0.01 eq) at 0°C. Stir 2 h. Wash with Na2S2O3 then brine. Dry, concentrate.",
        "Step 4 — Suzuki coupling: dissolve C2-Br indole in EtOH/H2O (4:1, 20 L). Add arylboronic acid (1.2 eq), K2CO3 (2.5 eq), Pd(PPh3)4 (3 mol%). Reflux under N2, 6 h.",
        "Step 5 — Filter Pd, concentrate, partition between EtOAc and brine. Dry, concentrate. Silica plug if needed.",
        "Step 6 — Detritylation: dissolve in EtOH (15 L), add 1M HCl in EtOH (2 eq), stir RT 4 h. Monitor by HPLC. Basify with K2CO3, extract EtOAc.",
        "Step 7 — Piperazine coupling: dissolve amine in anhydrous THF (15 L), add N-Boc-piperazinyl halide coupling partner (1.2 eq), DIPEA (3 eq), heat 60°C under N2, 12 h.",
        "Step 8 — Workup: dilute with EtOAc, wash with sat. NH4Cl, brine. Dry, concentrate.",
        "Step 9 — Boc removal: dissolve in EtOAc, add 4M HCl in EtOAc (3 eq), stir 2 h RT. Collect precipitated HCl salt by filtration.",
        "Step 10 — Recrystallize HCl salt twice from EtOH/Acetone (1:3) to achieve ≥99.5% HPLC purity.",
        "Step 11 — Final release testing: HPLC purity, chiral HPLC, residual solvents, Pd content <10 ppm by ICP-MS."
      ],
      yieldTarget: "10-15% overall across 7 steps (300 g per campaign)",
      standardSolventsUsed: ["EtOH", "EtOAc", "THF", "Heptane", "Acetone"],
      standardSolventsNotUsed: ["DMF replaced by THF in couplings", "Dioxane replaced by EtOH/H2O in Suzuki", "DCM replaced by EtOAc throughout"],
      hazards: "NaH: pyrophoric. NBS: strong oxidizer. Pd catalyst removal to <10 ppm requires activated-carbon treatment (Pd scavenger like SiliaMetS Thiol). Fluoroaromatics: potential accumulation in aquatic waste streams — carbon filter effluent. Final API is MOR agonist — Band 4 containment with naloxone stations."
    },
    safety: {
      ehs: "OEL ≤ 1 μg/m³; Containment Band 4. All opioid-family protocols apply (buddy system, naloxone access, medical surveillance).",
      toxicology: "Acute oral LD50 (mouse) ~250 mg/kg. Analgesic ED50 (warm-water tail-withdrawal) ~1.2 mg/kg IP. Respiratory depression ED50 >10 mg/kg — therapeutic index ~8× vs morphine's ~2×. Tolerance development after 5-day dosing minimal vs morphine. Physical dependence signs absent after naloxone challenge.",
      scheduling: "Unscheduled. Clinical development would trigger Schedule II."
    },
    wohlerScoring: {
      benefit: 74,
      harm: 40,
      net: 34,
      evidenceConfidence: "MEDIUM",
      rationale: "SR-14968 represents the best-characterized MOR-biased scaffold in the peer-reviewed literature, with in vivo evidence of the safer opioid profile. Primary value: structural template and SAR anchor for Wohler's biased MOR library. Medium confidence reflects ongoing bias-framework debate and limited non-Bohn-lab data."
    },
    references: [
      "Schmid C.L. et al. Cell 2017, 171, 1165-1175 (primary disclosure)",
      "Bohn L.M. et al. Science 1999, 286, 2495 (β-arrestin-2 KO establishes bias rationale)",
      "Raehal K.M. et al. J. Pharmacol. Exp. Ther. 2005, 314, 1195 (arrestin/respiratory link)",
      "Hill R. et al. Br. J. Pharmacol. 2018, 175, 2653 (bias framework critique)",
      "Gillis A. et al. Sci. Signal. 2020, 13, eaaz3140 (low-efficacy reframing)"
    ],
    nextSteps: [
      "Reproduce 7-step synthesis at 5 g scale to validate route",
      "Develop CDI-based piperazine coupling to avoid halide intermediates",
      "Run G-protein/β-arrestin bias assays (BRET) in-house to confirm bias factor",
      "Rat tail-flick analgesia and whole-body plethysmography safety margin",
      "5-day tolerance and precipitated withdrawal studies vs morphine",
      "Design 15-compound SAR library around the piperazine linker"
    ]
  },

  "oliceridine-comparator": {
    id: "oliceridine-comparator",
    name: "Oliceridine Comparator (TRV130)",
    fullName: "Oliceridine (TRV130) — FDA-approved G-protein biased MOR agonist, Wohler reference compound",
    aliases: ["TRV130", "Olinvyk"],
    categoryId: "opioid",
    program: "Program D — Biased MOR / Safer Opioid",
    cas: "1401028-24-7",
    formula: "C22H30N2O2S",
    molecularWeight: 386.55,
    schedulingStatus: "Schedule II (US) — FDA approved August 2020 as Olinvyk® (IV)",
    schedulingNote: "Oliceridine is Schedule II CSA. Used by Wohler strictly as a reference/comparator compound; no development intent.",
    benefitScore: 68,
    harmScore: 48,
    evidenceConfidence: "HIGH",
    status: "Reference / Comparator",
    statusColor: "blue",
    goDecision: "REFERENCE — not a Wohler development candidate",
    priorityTier: "Reference — Comparator Use Only",
    summary: "Oliceridine (Trevena TRV130, marketed as Olinvyk) is the first FDA-approved G-protein biased MOR agonist for acute moderate-to-severe pain in hospital settings (IV). Bias factor ~3-6 vs morphine. Post-approval data show modestly reduced respiratory depression and nausea/vomiting vs morphine at equianalgesic doses, though real-world benefit remains debated. Wohler uses oliceridine as the benchmark comparator for all Program D candidates.",
    pros: [
      "FDA-approved (August 2020) — validates G-protein biased MOR concept in humans",
      "IV formulation avoids GI side-effects at drug/receptor level",
      "Modest reduction in respiratory depression and emesis vs morphine in Phase 3",
      "Proven manufacturing scale (Trevena supply chain)",
      "Definitive bias-factor measurement point for Wohler internal benchmarking"
    ],
    cons: [
      "Post-approval real-world benefit more modest than initial claims",
      "Re-classification as low-efficacy partial agonist (Gillis 2020) rather than biased agonist",
      "IV-only formulation limits market",
      "Schedule II — full abuse liability potential",
      "Limited chronic-pain data; narrow acute-pain indication",
      "Trevena patent protected — no Wohler development path"
    ],
    pharmacology: {
      mechanismOfAction: "Low-efficacy partial agonism at MOR with reduced β-arrestin-2 recruitment. Originally marketed as 'biased' — current interpretation emphasizes low intrinsic efficacy as the primary safety driver.",
      targets: [
        { target: "MOR", affinity: "Ki = 0.82 nM", activity: "Partial agonist (Emax ~70% of DAMGO at G-protein)" },
        { target: "KOR", affinity: "Ki = 230 nM", activity: "Weak activity" },
        { target: "DOR", affinity: "Ki >1 μM", activity: "Inactive" },
        { target: "β-arrestin-2", affinity: "EC50 >100 nM", activity: "Reduced recruitment — bias factor ~3-6" },
        { target: "hERG", affinity: "IC50 ~14 μM", activity: "Acceptable margin" }
      ],
      onsetDuration: "IV: onset 1-3 min; t½ ~1.5-3 h; analgesic duration 1-3 h",
      metabolism: "CYP3A4 and CYP2D6 — known DDI with strong inhibitors of either pathway."
    },
    labSynthesis: {
      overview: "Not synthesized by Wohler — reference compound is purchased as Olinvyk API standard from certified suppliers. Retrosynthesis is documented in Trevena filings: three-step convergent synthesis from a thiophene-spirocyclic core, a chromanyl amine, and a methylenation coupling.",
      steps: [
        "Step 1 — Suzuki coupling of thiophene boronic ester with a chromene partner (Pd(OAc)2, PPh3, K2CO3, EtOH/H2O).",
        "Step 2 — Asymmetric reductive amination with the spirocyclic ketone using chiral phosphoramidite-modified Ru catalyst in IPA/H2O.",
        "Step 3 — Salt formation (HCl) and recrystallization from EtOH/Acetone.",
        "NOTE: Wohler does not manufacture oliceridine; this entry reflects reference/comparator use only. If manufacturing were required, a licensed route from Trevena would be necessary."
      ],
      analytics: "Purchased reference standard — COA from supplier sufficient. Internal HPLC verification vs USP standard."
    },
    kgScaleSynthesis: {
      overview: "Not applicable — Wohler does not manufacture oliceridine. Reference compound procured from qualified API suppliers (certified Ph.Eur / USP grade).",
      targetScale: "N/A — reference use only (50-100 g stock for internal assays and in vivo comparators)",
      steps: [
        "Procurement only — qualify certified supplier, verify COA, perform internal HPLC identity and purity testing."
      ],
      yieldTarget: "N/A",
      standardSolventsUsed: ["N/A — not manufactured"],
      standardSolventsNotUsed: ["N/A"],
      hazards: "As Schedule II API: DEA Schedule II inventory requirements, alarmed safe storage, dual-witnessed access log, biennial inventory reconciliation."
    },
    safety: {
      ehs: "OEL ≤ 1 μg/m³; Containment Band 4. Schedule II handling protocols. Naloxone stations mandatory.",
      toxicology: "FDA-approved human safety profile. Respiratory depression remains the primary serious adverse event. Therapeutic index in Phase 3 approximately 1.5-2× better than morphine on respiratory adverse-event rate at equianalgesia.",
      scheduling: "Schedule II CSA (US). DEA registration required for all handling."
    },
    wohlerScoring: {
      benefit: 68,
      harm: 48,
      net: 20,
      evidenceConfidence: "HIGH",
      rationale: "Oliceridine's value to Wohler is exclusively as an FDA-approved benchmark comparator that validates the G-protein bias concept. Not a development candidate (IP-locked, Schedule II, modest real-world benefit). High-confidence scoring due to full Phase 3 and post-approval real-world data."
    },
    references: [
      "DeWire S.M. et al. J. Pharmacol. Exp. Ther. 2013, 344, 708 (TRV130 primary)",
      "Singla N.K. et al. Pain Pract. 2019, 19, 715 (APOLLO-1 Phase 3)",
      "Viscusi E.R. et al. Pain 2016, 157, 264 (Phase 2 dose-finding)",
      "FDA Briefing Document for NDA 210730 (2018/2020)",
      "Gillis A. et al. Sci. Signal. 2020, 13, eaaz3140 (low-efficacy reframing)"
    ],
    nextSteps: [
      "Maintain 50-100 g reference stock from qualified supplier",
      "Use as primary in vivo comparator for all Wohler Program D candidates",
      "Benchmark bias factors (BRET) of Wohler compounds vs oliceridine reference",
      "Cite in Wohler's TPP (Target Product Profile) for biased MOR program"
    ]
  },

  "brorphine-warning-class": {
    id: "brorphine-warning-class",
    name: "Brorphine Warning Class",
    fullName: "Brorphine and benzimidazolone nitazene-adjacent opioids — regulatory warning class",
    aliases: ["Brorphine", "Benzimidazolone opioids"],
    categoryId: "opioid",
    program: "Program D — Biased MOR / Safer Opioid",
    cas: "2244737-98-0",
    formula: "C21H24BrN3O",
    molecularWeight: 414.34,
    schedulingStatus: "Schedule I (US) — emergency scheduled 2020, permanent 2023",
    schedulingNote: "Brorphine is Schedule I in the US, UK, and multiple EU states. Wohler tracks this class as a REGULATORY WARNING and DO-NOT-SYNTHESIZE example — NOT a development target.",
    benefitScore: 8,
    harmScore: 96,
    evidenceConfidence: "HIGH",
    status: "DO-NOT-DEVELOP — Regulatory Warning",
    statusColor: "red",
    goDecision: "NO-GO — regulatory and safety contraindication",
    priorityTier: "Regulatory Awareness — Avoid",
    summary: "Brorphine and structurally related benzimidazolone (and broader nitazene-adjacent) opioids are potent full MOR agonists with fentanyl-class overdose potential. First identified as a recreational adulterant 2019-2020, brorphine was emergency-scheduled in the US in 2020 and associated with multiple fatal overdoses. Wohler maintains this entry as a REGULATORY AWARENESS record — the structural class is documented here so that Program D SAR explicitly designs AWAY from it.",
    pros: [
      "None — this is a DO-NOT-SYNTHESIZE regulatory-awareness entry"
    ],
    cons: [
      "Full high-efficacy MOR agonism — extreme respiratory depression risk",
      "Fentanyl-class potency (ED50 ~0.05 mg/kg mouse)",
      "No β-arrestin bias — worst-case safety profile",
      "Schedule I in US, UK, EU — criminal liability for possession/synthesis",
      "Associated with >30 confirmed overdose deaths 2019-2021",
      "No therapeutic rationale — emerged as novel psychoactive substance (NPS)"
    ],
    pharmacology: {
      mechanismOfAction: "Full high-efficacy MOR agonist with robust β-arrestin-2 recruitment. No bias — recruits both signaling arms maximally, driving respiratory depression at low doses.",
      targets: [
        { target: "MOR", affinity: "Ki ~2 nM", activity: "Full agonist (Emax equivalent to DAMGO, both G-protein and β-arrestin)" },
        { target: "KOR", affinity: "Ki ~500 nM", activity: "Moderate partial agonist" },
        { target: "DOR", affinity: "Ki ~200 nM", activity: "Moderate agonist" },
        { target: "hERG", affinity: "IC50 data limited", activity: "Uncharacterized" }
      ],
      onsetDuration: "Rapid onset; high potency — respiratory arrest within minutes at recreational street doses",
      metabolism: "Limited data — CYP-mediated N-dealkylation and aromatic oxidation likely"
    },
    labSynthesis: {
      overview: "WOHLER DOES NOT SYNTHESIZE COMPOUNDS IN THIS CLASS. This entry exists solely to document the structural class for exclusion from SAR work.",
      steps: [
        "DO NOT SYNTHESIZE — Schedule I controlled substance",
        "Any structural analog with: (a) benzimidazolone core + (b) N-piperidine linker + (c) 4-bromophenethyl substituent must be flagged by Wohler's internal SAR review board as a HARD EXCLUSION."
      ],
      analytics: "N/A — not synthesized"
    },
    kgScaleSynthesis: {
      overview: "NOT APPLICABLE — Schedule I compound with no therapeutic rationale. Wohler will not synthesize or process any compound in this class.",
      targetScale: "ZERO — DO NOT MANUFACTURE",
      steps: [
        "HARD EXCLUSION — all Wohler Program D SAR libraries are screened against a structural alert database that flags benzimidazolone + piperidine + halophenethyl patterns",
        "Compounds flagged are removed from synthesis queues and reported to the Wohler regulatory affairs team"
      ],
      yieldTarget: "N/A",
      standardSolventsUsed: ["N/A"],
      standardSolventsNotUsed: ["N/A"],
      hazards: "Schedule I controlled substance. Unauthorized possession is a federal felony."
    },
    safety: {
      ehs: "N/A — not handled",
      toxicology: "Fatal overdose risk at sub-milligram doses. Multiple confirmed fatal poisonings in forensic toxicology literature 2020-2022.",
      scheduling: "Schedule I US (permanent 2023), UK (Class A), EU (multiple states), UNODC-watched."
    },
    wohlerScoring: {
      benefit: 8,
      harm: 96,
      net: -88,
      evidenceConfidence: "HIGH",
      rationale: "Pure NO-GO regulatory warning entry. Zero therapeutic value, maximum harm. Included in Wohler's database to ensure SAR design explicitly avoids this structural class. Functions as a negative exemplar for the Program D internal safety review."
    },
    references: [
      "DEA Final Rule: Placement of Brorphine in Schedule I (2023)",
      "Vandeputte M.M. et al. Drug Test. Anal. 2020, 12, 1382 (brorphine pharmacology)",
      "Krotulski A.J. et al. J. Anal. Toxicol. 2021, 45, 850 (forensic overdose cases)",
      "UNODC Early Warning Advisory: Brorphine (2020)"
    ],
    nextSteps: [
      "Maintain this entry as part of Wohler's internal structural-alert database",
      "Review all Program D library designs against the brorphine structural motif before synthesis",
      "Report any accidental structural convergence to regulatory affairs immediately",
      "Use in SAR education for all Wohler chemists working in Program D"
    ]
  },

  "peripheral-kor-agonists": {
    id: "peripheral-kor-agonists",
    name: "Peripheral KOR Agonists",
    fullName: "Peripherally restricted kappa-opioid receptor agonists — analgesia without CNS dysphoria",
    aliases: ["CR845", "Difelikefalin", "Asimadoline", "FE 200665"],
    categoryId: "opioid",
    program: "Program D — Biased MOR / Safer Opioid",
    cas: "1024828-77-0 (difelikefalin)",
    formula: "C36H53N7O6 (difelikefalin)",
    molecularWeight: 679.84,
    schedulingStatus: "Unscheduled (difelikefalin approved 2021 as Korsuva®)",
    schedulingNote: "Difelikefalin (Korsuva) is not a controlled substance — peripheral-only activity eliminates abuse potential. Sets precedent that peripheral KOR agonism is a viable non-controlled analgesic path.",
    benefitScore: 76,
    harmScore: 25,
    evidenceConfidence: "HIGH",
    status: "Priority 1",
    statusColor: "green",
    goDecision: "GO — peripheral KOR analgesia program",
    priorityTier: "Tier 1 — Active Research",
    summary: "Peripherally restricted KOR agonists (difelikefalin/CR845, asimadoline) achieve analgesia and anti-pruritic effects without crossing the blood-brain barrier, thereby avoiding KOR-mediated central dysphoria, sedation, and hallucinations. Difelikefalin (Korsuva) was FDA-approved in 2021 for uremic pruritus and validated the peripheral-KOR strategy in humans. Wohler pursues peripheral-KOR as a non-controlled, non-MOR analgesic pathway.",
    pros: [
      "Difelikefalin (FDA-approved 2021) validates peripheral-KOR strategy in humans",
      "No controlled-substance scheduling — no abuse liability",
      "No respiratory depression (KOR does not drive MOR-type respiratory effects)",
      "No constipation at therapeutic doses (peripheral restriction spares CNS/GI)",
      "Peptide-based (difelikefalin) and small-molecule (asimadoline) templates both available",
      "Strong IP landscape — clean design-around opportunities"
    ],
    cons: [
      "Peptide delivery (difelikefalin is IV/SC only) — no oral option currently",
      "Analgesic efficacy more modest than MOR agonists in somatic pain",
      "Tolerance development at peripheral KOR over chronic dosing",
      "Niche indications (uremic pruritus) vs broad pain market",
      "Asimadoline failed in Phase 3 IBS (efficacy not met)"
    ],
    pharmacology: {
      mechanismOfAction: "Selective KOR full agonism at peripheral sensory nerve terminals and dorsal root ganglion neurons. Peripheral restriction achieved via: (a) high polarity/charge (difelikefalin is a D-amino acid tetrapeptide), or (b) P-gp-mediated efflux at the BBB. KOR activation peripherally inhibits primary afferent nociceptor firing and mast-cell histamine release (pruritus).",
      targets: [
        { target: "KOR (peripheral)", affinity: "Ki ~0.3 nM (difelikefalin)", activity: "Full agonist — high efficacy at G-protein signaling" },
        { target: "MOR", affinity: "Ki >1 μM", activity: "Inactive — critical for non-controlled status" },
        { target: "DOR", affinity: "Ki >1 μM", activity: "Inactive" },
        { target: "BBB penetration", affinity: "Brain/plasma ratio <0.01 (difelikefalin)", activity: "Excluded from CNS" }
      ],
      onsetDuration: "IV difelikefalin: onset 15-30 min; t½ ~2-3 h; duration 4-6 h. Oral asimadoline: t½ ~7 h",
      metabolism: "Difelikefalin is a peptide — renal excretion unchanged (>70% of dose). Asimadoline: CYP3A4 oxidation."
    },
    labSynthesis: {
      overview: "Difelikefalin synthesis is SPPS (solid-phase peptide synthesis) of a D-amino-acid tetrapeptide (D-Phe-D-Phe-D-Leu-D-Lys) with a C-terminal 4-aminopiperidine-4-carboxylic acid. Asimadoline is small-molecule and synthesized in 5 steps from L-phenylalanine derivatives.",
      steps: [
        "Difelikefalin (representative peptide route):",
        "Step 1 — SPPS on Wang resin using Fmoc-D-Lys(Boc)-OH as first residue. Couple with HBTU/HOBt in DMF (Wohler alternative at scale: switch to Oxyma Pure/DIC in EtOAc/THF).",
        "Step 2 — Sequential coupling of Fmoc-D-Leu-OH, Fmoc-D-Phe-OH, Fmoc-D-Phe-OH with Fmoc deprotection (20% piperidine in DMF or Wohler's MeTHF alternative) between each.",
        "Step 3 — Cap with 4-amino-1-carbamoylpiperidine-4-carboxylic acid (special building block).",
        "Step 4 — Global deprotection and cleavage from resin with TFA/water/triisopropylsilane (95:2.5:2.5), 2 h RT.",
        "Step 5 — Precipitate in cold Et2O (Wohler alt: cold Heptane). Reversed-phase HPLC purification (C18, H2O/MeCN/0.1% TFA).",
        "Step 6 — Lyophilize to give difelikefalin acetate.",
        "Characterization: HRMS, analytical HPLC ≥99%, amino-acid analysis, chiral integrity by D-AA analysis."
      ],
      analytics: "HRMS (ESI+), analytical HPLC-UV ≥99%, amino acid composition, optical rotation, MALDI-TOF for intact mass."
    },
    kgScaleSynthesis: {
      overview: "Kg-scale peptide synthesis is specialty-CMO work (PolyPeptide, Bachem, etc.). Wohler strategy: contract manufacture for peptide KOR agonists; focus internal synthesis on small-molecule peripherally restricted KOR agonists (asimadoline-class).",
      targetScale: "100 g asimadoline-class small-molecule per campaign (peptides outsourced to CMO)",
      steps: [
        "For asimadoline-class small molecule:",
        "Step 1 — Start with Boc-L-phenylalaninol (2.0 kg, commercial).",
        "Step 2 — Oxidation to aldehyde (TEMPO/NaOCl in EtOAc/H2O biphasic) — Wohler-compliant.",
        "Step 3 — Reductive amination with N-methyl-N-phenethylamine (NaBH(OAc)3 in EtOAc with 5% AcOH).",
        "Step 4 — Boc removal (4M HCl in EtOAc).",
        "Step 5 — Amide coupling with 1-(1-pyrrolidinyl)-cyclohexanecarboxylic acid using HATU/DIPEA in THF, 18 h RT.",
        "Step 6 — Silica plug, then crystallize from EtOH/Heptane. HCl salt formation, recrystallize from EtOH/Acetone.",
        "Step 7 — Final release: HPLC ≥99.5%, chiral HPLC ≥99% ee, residual solvents.",
        "Peptide variants (difelikefalin-class):",
        "- Contract with SPPS CMO (PolyPeptide Group / Bachem).",
        "- Provide Wohler-standard specs and release testing criteria.",
        "- Require kg-scale SPPS with Oxyma/DIC chemistry (greener than HBTU/HOBt)."
      ],
      yieldTarget: "Small molecule: 45-55% overall across 5 steps (100 g per campaign). Peptide: 18-25% overall (CMO).",
      standardSolventsUsed: ["EtOAc", "THF", "EtOH", "Heptane", "Acetone", "IPA"],
      standardSolventsNotUsed: ["DMF replaced by EtOAc/THF biphasic oxidation and THF couplings", "DCM replaced by EtOAc"],
      hazards: "NaBH(OAc)3: moisture-sensitive — handle under N2. HATU: sensitizer — closed handling. Peptide CMO work managed under quality agreement."
    },
    safety: {
      ehs: "OEL 10-50 μg/m³ (non-controlled, non-MOR) — Containment Band 2-3. Standard GMP handling sufficient.",
      toxicology: "Difelikefalin: FDA-approved safety profile. Main AEs: dizziness, transient BP changes, diarrhea. Asimadoline: well-tolerated in Phase 2/3 IBS trials (efficacy failure, not safety). No abuse liability observed in human studies.",
      scheduling: "Unscheduled. Critical advantage of the class."
    },
    wohlerScoring: {
      benefit: 76,
      harm: 25,
      net: 51,
      evidenceConfidence: "HIGH",
      rationale: "Peripheral-KOR is a clinically validated, non-controlled analgesic strategy. Difelikefalin approval proves the FDA path. Strong fit for Wohler's mission of safer analgesics without controlled-substance baggage. High-confidence scoring due to approved drug precedent plus substantial literature."
    },
    references: [
      "Deeks E.D. Drugs 2022, 82, 681 (difelikefalin approval review)",
      "Fishbane S. et al. N. Engl. J. Med. 2020, 382, 222 (KALM-1 pivotal trial)",
      "Arendt-Nielsen L. et al. Scand. J. Pain 2009 (peripheral KOR analgesia)",
      "Beck T.C. et al. Med. Sci. 2019, 7, 95 (peripheral KOR mechanism review)",
      "Mangel A.W. et al. Aliment. Pharmacol. Ther. 2008, 28, 239 (asimadoline Phase 2 IBS)"
    ],
    nextSteps: [
      "Design 20-compound peripherally restricted KOR small-molecule library",
      "Primary screen: KOR binding (Ki), G-protein activation (BRET), MOR counter-screen",
      "PAMPA and MDCK-MDR1 assays to confirm peripheral restriction",
      "In vivo: rat formalin paw model (peripheral nociception) + rotarod (CNS exclusion)",
      "Lead candidate: advance to GLP tox and Pre-IND package",
      "Target indication: post-operative pain (non-controlled alternative to opioids)"
    ]
  }

};